import { z } from 'zod'
import { ArchivableSchema } from '../archive'
import { AuditPropsSchema } from '../common'
import { CustomFieldTypeSchema } from '../customFieldMap'
import { AccountStatusIdSchema, WorkflowIdSchema, WorkflowStageIdSchema } from '../ids'
import { RoleIdSchema } from '../role'
import { type Prettify } from '../util'
import { CustomFieldCalculationSchema } from './calculation'
import { CustomFieldIdSchema } from './id'

export type CustomFieldType = z.infer<typeof CustomFieldTypeSchema>
export type DateCustomFieldValue = string | number
export const CUSTOM_FIELD_TYPE_NAMES = [
  'number',
  'string',
  'boolean',
  'date',
  'calculation',
  'multipleChoice',
  'attachment',
  'url',
] as const
export const CustomFieldTypeNameSchema = z.enum(CUSTOM_FIELD_TYPE_NAMES)
export type CustomFieldTypeName = z.infer<typeof CustomFieldTypeNameSchema>

export const CUSTOM_TABLE_TYPES = ['Account', 'Team', 'User'] as const
export const CustomTableTypeSchema = z.enum(CUSTOM_TABLE_TYPES)
export type CustomTableType = z.infer<typeof CustomTableTypeSchema>
export const isCustomFieldTableType = (value: string): value is CustomTableType => {
  return CustomTableTypeSchema.safeParse(value).success
}

export const CustomFieldFormatSchema = z.object({
  prefix: z.string().optional(),
  suffix: z.string().optional(),
  decimalPlaces: z
    .number()
    .optional()
    .describe('Number of decimal places, only applies to number fields, 0 by default'),
  delimiter: z.string().optional().describe('Delimiter, only applies to number fields, "," by default'),
  separator: z.string().optional().describe('Separator, only applies to number fields, "." by default'),
})
export type CustomFieldFormat = z.infer<typeof CustomFieldFormatSchema>

const CustomFieldBaseSchema = z.object({
  tableType: CustomTableTypeSchema,
  statusId: AccountStatusIdSchema.optional(),
  workflowStageId: WorkflowStageIdSchema.optional(),
  workflowIds: WorkflowIdSchema.array().optional(),
  label: z.string(),
  description: z.string().optional(),
  short: z.string().optional(),
  required: z.boolean().optional(),
  format: CustomFieldFormatSchema.optional(),
  viewRoleIds: RoleIdSchema.array().optional(),
  editRoleIds: RoleIdSchema.array().optional(),
  order: z.number().optional(),
  allowMultiple: z.literal(false).optional(),
})
export type BaseCustomField = z.infer<typeof CustomFieldBaseSchema>

export const StandardCustomFieldTypeSchema = CustomFieldTypeNameSchema.exclude([
  'calculation',
  'multipleChoice',
  'attachment',
])
export type StandardCustomFieldType = z.infer<typeof StandardCustomFieldTypeSchema>
export const StandardCustomFieldSchema = z.object({
  ...CustomFieldBaseSchema.shape,
  fieldType: StandardCustomFieldTypeSchema,
})

const CalculationCustomFieldSchema = z.object({
  ...CustomFieldBaseSchema.shape,
  fieldType: CustomFieldTypeNameSchema.extract(['calculation']),
  calculation: CustomFieldCalculationSchema,
})

const AttachmentCustomFieldSchema = z.object({
  ...CustomFieldBaseSchema.shape,
  fieldType: CustomFieldTypeNameSchema.extract(['attachment']),
  allowMultiple: z.boolean().optional(),
})

const MultipleChoiceCustomFieldSchema = z.object({
  ...CustomFieldBaseSchema.shape,
  fieldType: CustomFieldTypeNameSchema.extract(['multipleChoice']),
  options: z.array(z.string()),
  allowMultiple: z.boolean().optional(),
})

export const UnsavedCustomFieldSchema = z.discriminatedUnion('fieldType', [
  StandardCustomFieldSchema,
  CalculationCustomFieldSchema,
  AttachmentCustomFieldSchema,
  MultipleChoiceCustomFieldSchema,
])
export type UnsavedCustomField = z.infer<typeof UnsavedCustomFieldSchema>

export const CustomFieldDataSchema = UnsavedCustomFieldSchema.and(AuditPropsSchema)
  .and(ArchivableSchema)
  .and(
    z.object({
      fieldId: CustomFieldIdSchema,
    })
  )

export type CustomFieldData = Prettify<z.infer<typeof CustomFieldDataSchema>>
export type StandardCustomFieldData = Extract<CustomFieldData, { fieldType: StandardCustomFieldType }>
export type CalculationCustomFieldData = Extract<CustomFieldData, { fieldType: 'calculation' }>
export type AttachmentCustomFieldData = Extract<CustomFieldData, { fieldType: 'attachment' }>
export type MultipleChoiceCustomFieldData = Extract<CustomFieldData, { fieldType: 'multipleChoice' }>

export const CustomFieldSearchResultSchema = z.object({
  id: CustomFieldIdSchema,
  value: CustomFieldTypeSchema,
})
export type CustomFieldSearchResult = z.infer<typeof CustomFieldSearchResultSchema>
